import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Hero from "../components/Hero";
import AntarcticaPolarMap from "../components/AntarcticaMap";
import { fetchSaleStatus, fetchTopTokens, fetchAuctionInfo } from "../lib/api";
import { localized } from "../lib/i18nHelpers";
import { ArrowRight, Gavel, MapPin, Coins, ShieldCheck } from "lucide-react";

const RARITY_COLOR = {
    ultra_legendary: "text-pink-400 border-pink-400/40",
    legendary: "text-amber-300 border-amber-300/50",
    mythic: "text-emerald-400 border-emerald-400/40",
    epic: "text-frost border-frost/40",
};

const MARQUEE_REPEATS = ["a", "b"];

export default function HomePage() {
    const { t, i18n } = useTranslation();
    const [status, setStatus] = useState(null);
    const [tops, setTops] = useState([]);
    const [auctionData, setAuctionData] = useState({});

    useEffect(() => {
        fetchSaleStatus().then(setStatus);
        fetchTopTokens().then(async (list) => {
            setTops(list);
            const map = {};
            for (const tk of list) map[tk.number] = await fetchAuctionInfo(tk.number);
            setAuctionData(map);
        });
        // imports are module-level constants and intentionally stable; this effect runs once on mount
    }, []);  // eslint-disable-line react-hooks/exhaustive-deps

    const liveTops = tops.filter((tk) => tk.is_open);
    const previewTops = tops.slice(0, 6);

    return (
        <div data-testid="home-page">
            <Hero status={status} />

            <section className="border-y border-white/10 py-4 overflow-hidden">
                <div className="flex marquee whitespace-nowrap gap-12 text-xs uppercase tracking-[0.3em] text-slate-500">
                    {MARQUEE_REPEATS.map((id) => (
                        <div key={`marquee-${id}`} className="flex gap-12 shrink-0">
                            <span>· {t("marquee.parcels")}</span>
                            <span className="text-frost">· {t("marquee.size")}</span>
                            <span>· {t("marquee.network")}</span>
                            <span className="text-gold">· {t("marquee.top")}</span>
                            <span>· {t("marquee.usdc")}</span>
                            <span className="text-frost">· {t("marquee.direct")}</span>
                        </div>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="section-map">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-frost mb-4">
                            {t("home.map_kicker")}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
                            {t("home.map_title_1")}<br />
                            <span className="italic">{t("home.map_title_2")}</span>
                        </h2>
                        <p className="text-base font-mono text-slate-300 mt-6 max-w-md leading-relaxed">
                            {t("home.map_text")}
                        </p>
                        <Link
                            to="/explorer"
                            data-testid="map-explore-cta"
                            className="mt-8 inline-flex btn-sharp items-center gap-2 border border-frost/40 text-frost px-5 py-3 text-xs hover:bg-frost/10"
                        >
                            {t("home.map_cta")} <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                    </div>
                    <div className="md:col-span-7">
                        <AntarcticaPolarMap topTokens={tops} height={560} />
                    </div>
                </div>
            </section>

            <section className="relative py-24 border-y border-white/10" data-testid="section-top-auctions">
                <div className="absolute inset-0 opacity-15 bg-cover bg-center"
                    style={{ backgroundImage: "url(/img/topography.jpg)" }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-obsidian via-obsidian/90 to-obsidian" />
                <div className="relative max-w-7xl mx-auto px-4 md:px-8">
                    <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] text-gold mb-3 flex items-center gap-2">
                                <Gavel className="w-3 h-3" /> {t("home.top_kicker")} · {t("home.top_live", { n: liveTops.length })}
                            </div>
                            <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-2xl">
                                {t("home.top_title_1")}<br />
                                <span className="italic text-gold-bright">{t("home.top_title_2")}</span>
                            </h2>
                        </div>
                        <Link to="/auctions" data-testid="view-all-auctions"
                            className="btn-sharp inline-flex items-center gap-2 border border-gold text-gold px-4 py-3 text-xs hover:bg-[#d4a857]/10">
                            {t("home.top_view_all")} <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
                        {previewTops.map((tk) => {
                            const L = localized(tk, i18n);
                            return (
                            <Link key={tk.number} to="/auctions"
                                data-testid={`top-auction-card-${tk.number}`}
                                className="group bg-slate-900/60 backdrop-blur-md border border-white/10 hover:border-amber-200/50 hover:shadow-[0_0_24px_rgba(252,211,77,0.15)] transition-all duration-300 flex flex-col">
                                <div className="aspect-square border-b border-white/10 relative overflow-hidden bg-slate-950 flex items-center justify-center">
                                    <div className={`text-[9px] uppercase tracking-widest absolute top-2 left-2 px-2 py-1 border ${RARITY_COLOR[tk.rarity] || RARITY_COLOR.epic}`}>
                                        {t(`auctions.rarity.${tk.rarity}`)}
                                    </div>
                                    <div className="font-serif text-3xl text-center px-2 group-hover:scale-105 transition-transform duration-500">
                                        {L.title}
                                    </div>
                                </div>
                                <div className="p-3">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-slate-500">#{tk.number}</span>
                                        <span className="text-gold font-mono">
                                            {(tk.price || 0).toLocaleString()} USDC
                                        </span>
                                    </div>
                                </div>
                            </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 md:px-8 py-24">
                <div className="text-xs uppercase tracking-[0.3em] text-frost mb-4">
                    {t("home.steps_kicker")}
                </div>
                <h2 className="font-serif text-4xl md:text-5xl tracking-tight max-w-2xl">
                    {t("home.steps_title_1")} <span className="italic">{t("home.steps_title_2")}</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-px mt-16 bg-white/10 border border-white/10">
                    {[
                        { icon: ShieldCheck, n: "01", title: t("home.step1_title"), desc: t("home.step1_desc") },
                        { icon: MapPin, n: "02", title: t("home.step2_title"), desc: t("home.step2_desc") },
                        { icon: Coins, n: "03", title: t("home.step3_title"), desc: t("home.step3_desc") },
                    ].map((s) => (
                        <div key={s.n} className="bg-slate-950 p-8 md:p-12">
                            <s.icon className="w-6 h-6 text-frost mb-6" />
                            <div className="font-mono text-xs text-slate-500">{s.n}</div>
                            <div className="font-serif text-2xl mt-2">{s.title}</div>
                            <p className="font-mono text-sm text-slate-400 mt-3 leading-relaxed">{s.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 md:px-8 py-24" data-testid="section-tokenomics">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                    <div className="md:col-span-5">
                        <div className="text-xs uppercase tracking-[0.3em] text-frost mb-4">
                            {t("home.tokenomics_kicker")}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl tracking-tight">
                            {t("home.tokenomics_title_1")}<br />
                            <span className="italic">{t("home.tokenomics_title_2")}</span>
                        </h2>
                    </div>
                    <div className="md:col-span-7 space-y-px bg-white/10">
                        {[
                            { k: t("home.ea_label"), v: "2,000", p: "$25 USDC", desc: t("home.ea_desc") },
                            { k: t("home.ps_label"), v: "12,187", p: "$80 USDC", desc: t("home.ps_desc") },
                            { k: t("home.tt_label"), v: "13", p: t("home.tt_price"), desc: t("home.tt_desc") },
                        ].map((row) => (
                            <div key={row.k} className="grid grid-cols-12 gap-4 bg-slate-950 p-6 items-center">
                                <div className="col-span-12 md:col-span-3 font-serif text-2xl">{row.k}</div>
                                <div className="col-span-6 md:col-span-3 font-mono text-frost">{row.v}</div>
                                <div className="col-span-6 md:col-span-2 font-mono text-gold">{row.p}</div>
                                <div className="col-span-12 md:col-span-4 font-mono text-xs text-slate-400">{row.desc}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
